import {
  netClient,
  type CommonResponse,
  MEDIA_UPLOAD_AVATAR,
  MEDIA_UPLOAD_FILE,
  MEDIA_CHECK_CAP,
  MEDIA_STS_TOKEN,
  MEDIA_RESIZE
} from '@/apis/api'
import { closeLoading, openLoading } from '@/components/business/loading/loading'
import { handleErr } from '../exception/handler'
import i18n from '@/utils/i18n'
import { ref, type Ref } from 'vue'
import { ElMessage } from 'element-plus'
import { CODE_OK } from '@/apis/code'
import { renderMessage } from '@/utils/util'

import type OSS from 'ali-oss'

export interface ImageResponse {
  fileId: string
  image: FileDTO
}

export interface FileResponse {
  fileId: string
  file: FileDTO
}

export function uploadFile(file: File | Blob | null, fileName: string) {
  const formData = new FormData()
  if (file != null) {
    formData.append('file', file, fileName)
  }
  return netClient.formPut<CommonResponse<FileResponse>>(MEDIA_UPLOAD_FILE, formData)
}

export function checkStorageCap(size: number, serviceId: string) {
  return netClient.request(MEDIA_CHECK_CAP, 'get', { size: size, serviceId: serviceId })
}

export function uploadAvatar(file: Blob | null, fileName: string) {
  const formData = new FormData()
  if (file != null) {
    formData.append('image', file, fileName)
  }
  return netClient.formPut<CommonResponse<ImageResponse>>(MEDIA_UPLOAD_AVATAR, formData)
}

export interface FileDTO {
  id: string
  name: string
  url: string
  lowQuality: string
  middleQuality: string
  highQuality: string
  size: number
  ext: string
  w: number
  h: number
  type: number
  meta: Object | null
  createdAt: number
}

export const MEDIA_UNKOWN = 0
export const MEDIA_IMG = 1
export const MEDIA_VIDEO = 2
export const MEDIA_AUDIO = 4
export const MEDIA_DOC = 5

export const typeExtsMap = {
  video: ['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'mpg', 'mpeg'],
  audio: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'],
  image: ['jpg', 'jpeg', 'png', 'svg', 'webp'],
  gifv: ['gifv', 'gif']
}

export class FileUploader {
  private _file: File
  private _result: FileDTO | undefined

  constructor(file: File) {
    this._file = file
  }

  public get result() {
    return this._result
  }

  public async upload(callback: (result: FileDTO | null) => void) {
    openLoading(i18n.global.t('alt.uploading'))
    const { data, error, statusCode } = await uploadFile(this._file, this._file.name)
    closeLoading()
    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      const responseData = data as Ref<CommonResponse<FileResponse>>
      if (responseData.value.code == CODE_OK) {
        console.log('====ok====')
        this._result = responseData?.value.data.file
        callback(this._result)
      } else {
        ElMessage({
          message: renderMessage(responseData.value.message),
          type: 'warning'
        })
      }
    }
  }
}

export interface StorageStsSetting {
  region: string
  AccessKeyId: string
  AccessKeySecret: string
  bucket: string
  Expiration: number
  SecurityToken: string
}

export function getStsToken() {
  return netClient.request<CommonResponse<StorageStsSetting>>(MEDIA_STS_TOKEN, 'get', {})
}

export const stsSettings: Ref<StorageStsSetting | null> = ref(null)

export async function retrieveStsToken() {
  const { data, error, statusCode } = await getStsToken()
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as Ref<CommonResponse<StorageStsSetting>>
    if (resData.value.code === CODE_OK) {
      // success
      stsSettings.value = resData.value.data
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

export interface StsUploadResult {
  result: OSS.PutObjectResult | null
  error: any | null
}

export interface StsUploadPartialResult {
  result: OSS.MultipartUploadResult | null
  error: any | null
}

export async function uploadStsFile(file: File, name: string, client: OSS | null) {
  if (client === null) {
    return { result: null, error: new Error('no oss client') } as StsUploadResult
  }
  // console.log("region:", yourRegion, "accessKeyId:", yourAccessKeyId, "accessKeySecret:",
  //   yourAccessKeySecret, "securityToken:", yourSecurityToken, "bucket:", examplebucket);
  try {
    // 填写Object完整路径。Object完整路径中不能包含Bucket名称。
    // 您可以通过自定义文件名（例如exampleobject.txt）或文件完整路径（例如exampledir/exampleobject.txt）的形式实现将数据上传到当前Bucket或Bucket中的指定目录。
    // data对象可以自定义为file对象、Blob数据或者OSS Buffer。
    const options = {
      mime: file.type,
      headers: { 'Content-Type': file.type }
    }
    const result = await client?.put(name, file, options)
    console.log('oss upload:', result)
    return { result: result, error: null } as StsUploadResult
  } catch (e) {
    retrieveStsToken()
    // if (e.message.contains('SecurityTokenExpiredError')) {
    //   retrieveStsToken()
    // }
    return { result: null, error: e } as StsUploadResult
  }
}

export async function uploadStsFileByPiece(
  file: File,
  name: string,
  client: OSS | null,
  checkPoint: any,
  callback: (p: number, checkPoint: any, res: any) => void
) {
  if (client === null) {
    return { result: null, error: new Error('no oss client') } as StsUploadPartialResult
  }
  const headers = {
    // 指定Object的存储类型。
    'x-oss-storage-class': 'Standard',
    // 指定Object标签，可同时设置多个标签。
    'x-oss-tagging': 'Tag=lifepagecc',
    // 指定初始化分片上传时是否覆盖同名Object。此处设置为true，表示禁止覆盖同名Object。
    'x-oss-forbid-overwrite': 'true'
  }

  const options = {
    checkpoint: checkPoint,
    // 获取分片上传进度、断点和返回值。
    progress: (p: any, cpt: any, res: any) => {
      callback(p as number, cpt, res)
    },
    // 设置并发上传的分片数量。
    parallel: 1,
    // 设置分片大小。默认值为1 MB，最小值为100 KB。
    partSize: 1024 * 1024,
    // headers,
    // headers: headers,
    // 自定义元数据，通过HeadObject接口可以获取Object的元数据。
    mime: file.type
  }

  try {
    // 分片上传。
    const res = await client.multipartUpload(name, file, {
      ...options
    })
    console.log(res)
    return { result: res, error: null } as StsUploadPartialResult
  } catch (err) {
    console.log(err)
    return { result: null, error: null } as StsUploadPartialResult
  }
}

export function resizeImage(url: string, w: number, h: number) {
  return netClient.request<CommonResponse<string>>(MEDIA_RESIZE, 'get', { url: url, w: w, h: h })
}
